/**
 * Aggregatkatalog — real heater models whose datasheet drives the design
 * (issue 159).
 *
 * Every number below is copied from the manufacturer's product page named in
 * `source`, not derived from anything else in this repo. Where the page does
 * not state a value it is left out, and the consumer falls back to the
 * type's rule (`heater.clearance.*`) instead of guessing one.
 *
 * Safety distances are the manufacturer's distances to COMBUSTIBLE material.
 * The wall directly behind and beside the unit is covered by the strålskydd
 * the engines always build, so in practice these bind the lavar.
 *
 * Prices are NOT here: each model's is the `prices.heaterModel.<id>` rule in
 * `data/rules/prices.ts` (Swedish retail, with its shop as the rule's source),
 * so an admin can move it without a deploy and there is one number, not two.
 */
import type { HeaterType } from "../../engines/types";

/** Where the unit is fired from. `outside` = eldas utifrån, through the wall. */
export type HeaterFiring = "inside" | "outside";

export interface HeaterModelSource {
	url: string;
	/** ISO date the numbers were read off `url`. */
	checkedOn: string;
}

export interface HeaterModel {
	id: string;
	brand: string;
	name: string;
	heaterType: HeaterType;
	firing: HeaterFiring;
	powerKw: number;
	/** Manufacturer's recommended sauna room volume. */
	roomVolumeM3: { min: number; max: number };
	/**
	 * The body that stands in the sauna room. For an outside-fired unit this
	 * excludes the feed tunnel; the tunnel is `throughWallMm`.
	 */
	bodyMm: { width: number; depth: number; height: number };
	/**
	 * Outside-fired only: how far the feed tunnel reaches behind the body,
	 * i.e. the listed overall depth minus the in-room body depth.
	 */
	throughWallMm?: number;
	/** Distances to combustible material, as the source lists them. */
	clearanceMm: {
		side?: number;
		front?: number;
		back?: number;
		ceiling?: number;
	};
	/** Controls are built into the unit, so no separate styrpanel is bought. */
	integratedControl?: boolean;
	source: HeaterModelSource;
}

const CHECKED_ON = "2026-09-26";

export const HEATER_CATALOG: readonly HeaterModel[] = [
	{
		id: "harvia-m3",
		brand: "Harvia",
		name: "Harvia M3",
		heaterType: "wood",
		firing: "inside",
		powerKw: 16.5,
		roomVolumeM3: { min: 6, max: 13 },
		// 15.35 × 17.72 × 27.95 in on the page.
		bodyMm: { width: 390, depth: 450, height: 710 },
		clearanceMm: { side: 300, front: 300, back: 300, ceiling: 1200 },
		source: {
			url: "https://www.harvia.com/en-US/products/WKPM3/m3-black",
			checkedOn: CHECKED_ON,
		},
	},
	{
		id: "harvia-m3-sl",
		brand: "Harvia",
		name: "Harvia M3 SL",
		heaterType: "wood",
		firing: "outside",
		powerKw: 16.5,
		roomVolumeM3: { min: 6, max: 13 },
		// Listed overall 390 × 650 × 710 mm; the body is the M3's 450 deep, so
		// the feed tunnel is the remaining 200.
		bodyMm: { width: 390, depth: 450, height: 710 },
		throughWallMm: 200,
		clearanceMm: { side: 250, front: 300, back: 300, ceiling: 1200 },
		source: {
			url: "https://www.harvia.com/en-US/products/WKM3SL/m16sl",
			checkedOn: CHECKED_ON,
		},
	},
	{
		id: "harvia-legend-150",
		brand: "Harvia",
		name: "Harvia Legend 150",
		heaterType: "wood",
		firing: "inside",
		powerKw: 16,
		roomVolumeM3: { min: 6, max: 13 },
		bodyMm: { width: 530, depth: 530, height: 740 },
		clearanceMm: { side: 200, front: 500, back: 250, ceiling: 1000 },
		source: {
			url: "https://www.harvia.com/en/products/WK150LD/legend-150-black",
			checkedOn: CHECKED_ON,
		},
	},
	{
		id: "harvia-legend-150-sl",
		brand: "Harvia",
		name: "Harvia Legend 150 SL",
		heaterType: "wood",
		firing: "outside",
		powerKw: 16,
		roomVolumeM3: { min: 6, max: 13 },
		// Listed overall 530 × 730 × 740 mm; the Legend 150 body is 530 deep.
		bodyMm: { width: 530, depth: 530, height: 740 },
		throughWallMm: 200,
		clearanceMm: { side: 200, front: 500, back: 250, ceiling: 1000 },
		source: {
			url: "https://www.harvia.com/en-US/products/WK150LDSL/legend-150-sl-black",
			checkedOn: CHECKED_ON,
		},
	},
	{
		id: "harvia-vega-bc60",
		brand: "Harvia",
		name: "Harvia Vega BC60",
		heaterType: "electric",
		firing: "inside",
		powerKw: 6,
		roomVolumeM3: { min: 5, max: 8 },
		bodyMm: { width: 480, depth: 310, height: 540 },
		clearanceMm: { side: 50, front: 30, ceiling: 1100 },
		integratedControl: true,
		source: {
			url: "https://www.harvia.com/en/products/HCB600400S/vega-bc60-60-kw-steel",
			checkedOn: CHECKED_ON,
		},
	},
	{
		id: "harvia-vega-bc90",
		brand: "Harvia",
		name: "Harvia Vega BC90",
		heaterType: "electric",
		firing: "inside",
		powerKw: 9,
		roomVolumeM3: { min: 8, max: 14 },
		bodyMm: { width: 480, depth: 310, height: 540 },
		// The page lists no side distance; the electric rule applies.
		clearanceMm: { front: 40, ceiling: 1100 },
		integratedControl: true,
		source: {
			url: "https://www.harvia.com/en/products/HCB900400S/vega-bc90-90-kw-steel",
			checkedOn: CHECKED_ON,
		},
	},
];

const BY_ID = new Map(HEATER_CATALOG.map((model) => [model.id, model]));

/** A design's heater: the type, and optionally the catalog model chosen. */
export interface HeaterSelection {
	heaterType: HeaterType;
	heaterModelId?: string | null;
}

export function getHeaterModel(id: string | null | undefined) {
	return id ? BY_ID.get(id) : undefined;
}

/**
 * The model a design actually uses. A model of the other type is ignored, so
 * switching ved ↔ el never keeps a stale model's box, clearance or price.
 */
export function resolveHeaterModel(
	selection: HeaterSelection,
): HeaterModel | undefined {
	const model = getHeaterModel(selection.heaterModelId);
	return model && model.heaterType === selection.heaterType ? model : undefined;
}

export function heaterModelsFor(heaterType: HeaterType): HeaterModel[] {
	return HEATER_CATALOG.filter((model) => model.heaterType === heaterType);
}

export function normalizeHeaterModelId(value: unknown): string | null {
	return typeof value === "string" && BY_ID.has(value) ? value : null;
}

export function heaterModelPriceRuleId(model: HeaterModel): string {
	return `prices.heaterModel.${model.id}`;
}

export type HeaterModelFitStatus = "ok" | "undersized" | "oversized";

export interface HeaterModelFit {
	modelId: string;
	status: HeaterModelFitStatus;
	/** Translation key; null when the model suits the room. */
	messageKey:
		| "domain_heater_modelFit_undersized"
		| "domain_heater_modelFit_oversized"
		| null;
	args: { model: string; volumeM3: number; minM3: number; maxM3: number };
}

/**
 * Does the chosen model suit the room? Compared on the EFFECTIVE volume
 * (glass and bare stone counted as extra m³), which is how the manufacturers'
 * room-volume ranges are meant to be read.
 */
export function checkHeaterModelFit(
	model: HeaterModel,
	effectiveVolumeM3: number,
): HeaterModelFit {
	const { min, max } = model.roomVolumeM3;
	const status: HeaterModelFitStatus =
		effectiveVolumeM3 > max
			? "undersized"
			: effectiveVolumeM3 < min
				? "oversized"
				: "ok";
	return {
		modelId: model.id,
		status,
		messageKey:
			status === "undersized"
				? "domain_heater_modelFit_undersized"
				: status === "oversized"
					? "domain_heater_modelFit_oversized"
					: null,
		args: {
			model: model.name,
			volumeM3: Number(effectiveVolumeM3.toFixed(1)),
			minM3: min,
			maxM3: max,
		},
	};
}

/**
 * The kW actually installed: the chosen model's rating, else the size the
 * volume calls for. Fuse, cable and the glass check size against this.
 */
export function installedHeaterKw(
	selection: HeaterSelection,
	recommendedKw: number,
): number {
	return resolveHeaterModel(selection)?.powerKw ?? recommendedKw;
}
